import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import rewardsService from '../services/rewardsService';
import './RecompensaAbate.css';

const formatarValor = (v) =>
  (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/**
 * Painel de abate de recompensa numa Fatura tipo Fornecedor.
 * Mostra o saldo lido ao vivo, quanto dá para abater (min(saldo, taxa)) e um
 * botão que dispara o abate. Ao concluir, chama onAbatido() para o pai recarregar.
 *
 * Só faz sentido quando a fatura tem taxa a abater — o pai decide se renderiza.
 */
function RecompensaAbate({ faturaId, onAbatido }) {
  const [loading, setLoading] = useState(true);
  const [abatendo, setAbatendo] = useState(false);
  const [preview, setPreview] = useState(null);
  const [erro, setErro] = useState(null);

  const carregar = useCallback(async () => {
    try {
      setLoading(true);
      setErro(null);
      const data = await rewardsService.previewFatura(faturaId);
      setPreview(data);
    } catch (e) {
      setErro(e.response?.data?.message || 'Não foi possível consultar o saldo de recompensa');
    } finally {
      setLoading(false);
    }
  }, [faturaId]);

  useEffect(() => { carregar(); }, [carregar]);

  const handleAbater = async () => {
    if (abatendo) return;
    const abate = preview?.abatePossivel || 0;
    if (abate <= 0) return;
    if (!window.confirm(`Confirmar abate de ${formatarValor(abate)} de crédito de recompensa nesta fatura?`)) return;

    try {
      setAbatendo(true);
      const res = await rewardsService.abaterFatura(faturaId);
      if (res.abatido > 0) {
        toast.success(`Recompensa abatida: ${formatarValor(res.abatido)}`);
      } else {
        toast.info(res.message || 'Nada a abater no momento');
      }
      await carregar();
      if (onAbatido) onAbatido();
    } catch (e) {
      const status = e.response?.status;
      const msg = e.response?.data?.message;
      if (status === 202) toast.info(msg || 'Abate em processamento — será confirmado pela reconciliação');
      else if (status === 409) toast.warning(msg || 'Saldo mudou — tente novamente');
      else if (status === 404) toast.error(msg || 'CNPJ fora do programa de recompensas');
      else toast.error(msg || 'Falha ao abater recompensa');
      await carregar();
    } finally {
      setAbatendo(false);
    }
  };

  // Silencioso quando não há programa/saldo: não polui a tela de quem não participa.
  if (!loading && (erro || !preview)) return null;
  if (!loading && preview && !preview.saldo?.ok && preview.saldo?.notFound) return null;

  const saldo = preview?.saldo || {};
  const abate = preview?.abatePossivel || 0;
  const taxaRestante = preview?.taxaRestante || 0;

  return (
    <div className="recompensa-abate">
      <div className="recompensa-abate-header">
        <span className="recompensa-abate-titulo">🎁 Crédito de Recompensa</span>
        <button className="recompensa-abate-refresh" onClick={carregar} disabled={loading || abatendo} title="Atualizar saldo">
          ↻
        </button>
      </div>

      {loading ? (
        <div className="recompensa-abate-loading">Consultando saldo ao vivo…</div>
      ) : (
        <>
          <div className="recompensa-abate-grid">
            <div className="recompensa-abate-item">
              <span className="ra-label">Saldo disponível</span>
              <span className="ra-valor ra-verde">{formatarValor(saldo.disponivel)}</span>
            </div>
            <div className="recompensa-abate-item">
              <span className="ra-label">Taxa a abater</span>
              <span className="ra-valor">{formatarValor(taxaRestante)}</span>
            </div>
            <div className="recompensa-abate-item">
              <span className="ra-label">Abate possível</span>
              <span className="ra-valor ra-destaque">{formatarValor(abate)}</span>
            </div>
          </div>

          {saldo.pendente > 0 && (
            <div className="recompensa-abate-pendente">
              + {formatarValor(saldo.pendente)} pendente (vira saldo no fechamento mensal)
            </div>
          )}

          <button
            className="recompensa-abate-btn"
            onClick={handleAbater}
            disabled={abatendo || abate <= 0}
          >
            {abatendo ? 'Abatendo…' : abate > 0 ? `Abater ${formatarValor(abate)} na fatura` : 'Sem saldo para abater'}
          </button>
          <p className="recompensa-abate-nota">
            O abate reduz a taxa desta fatura — o valor a receber aumenta pelo mesmo montante.
          </p>
        </>
      )}
    </div>
  );
}

export default RecompensaAbate;
